import { useEffect, useState } from 'react';
import { clearPlayerCode, readPlayerCode, whoAmI, writePlayerCode } from '../obr/playerCode.ts';

type State =
  | { kind: 'checking' }
  | { kind: 'entry'; message: string }
  | { kind: 'connected'; label: string };

export interface PlayerCodePanelProps {
  roomId: string;
  /** Shown in the entry box when the panel opens, e.g. why the player was disconnected. */
  initialMessage?: string;
  onConnected: (code: string, label: string, playerId: string) => void;
  onDisconnected: () => void;
}

/**
 * Links this browser to the player's account on the sheet server using the
 * code their GM sent. A code that the server rejects is forgotten, so a
 * replaced or revoked code never lingers.
 */
export function PlayerCodePanel({ roomId, initialMessage = '', onConnected, onDisconnected }: PlayerCodePanelProps) {
  const [state, setState] = useState<State>(() => (readPlayerCode(roomId) ? { kind: 'checking' } : { kind: 'entry', message: initialMessage }));
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);

  async function check(code: string, remember: boolean) {
    setBusy(true);
    const result = await whoAmI(code, roomId);
    setBusy(false);
    if (result.ok) {
      if (remember && !writePlayerCode(roomId, code)) {
        setState({ kind: 'entry', message: 'This browser is blocking storage, so the code cannot be remembered.' });
        return;
      }
      setInput('');
      setState({ kind: 'connected', label: result.label });
      onConnected(code.trim(), result.label, result.playerId);
      return;
    }
    if (result.reason === 'invalid' || result.reason === 'other-room') clearPlayerCode(roomId);
    setState({ kind: 'entry', message: result.message });
  }

  useEffect(() => {
    const saved = readPlayerCode(roomId);
    if (saved) void check(saved, false);
  }, [roomId]);

  if (state.kind === 'checking') {
    return <section className="dcc-code-panel"><p className="dcc-code-help">Checking your player code…</p></section>;
  }

  if (state.kind === 'connected') {
    return (
      <section className="dcc-code-panel dcc-code-connected">
        <p><strong>Connected as {state.label}</strong></p>
        <button
          className="dcc-code-forget"
          onClick={() => {
            clearPlayerCode(roomId);
            setState({ kind: 'entry', message: 'Code forgotten on this browser.' });
            onDisconnected();
          }}
        >
          Forget code on this browser
        </button>
      </section>
    );
  }

  return (
    <section className="dcc-code-panel">
      <h2>Player code</h2>
      <p className="dcc-code-help">Enter the code your GM sent you. You only need to do this once per browser.</p>
      <div className="dcc-code-row">
        <input
          className="dcc-code-input"
          value={input}
          placeholder="XXXX-XXXX-XXXX-XXXX"
          autoComplete="off"
          spellCheck={false}
          onChange={(e) => setInput(e.currentTarget.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && input.trim() && !busy) void check(input, true); }}
        />
        <button disabled={busy || !input.trim()} onClick={() => void check(input, true)}>
          {busy ? 'Checking…' : 'Connect'}
        </button>
      </div>
      {state.message && <p className="dcc-code-message" role="alert">{state.message}</p>}
    </section>
  );
}
